import { HStack, Icon, Text } from '@chakra-ui/react';
import { IconType } from '@react-icons/all-files';
import { FaBan } from '@react-icons/all-files/fa/FaBan';
import { FaRegClock } from '@react-icons/all-files/fa/FaRegClock';
import { FaTimesCircle } from '@react-icons/all-files/fa/FaTimesCircle';
import { FaUndo } from '@react-icons/all-files/fa/FaUndo';

import { formatExpiresIn } from '../../../../../../utils/dates';
import { formatMoney } from '../../../../../../utils/helpers';
import { Pill } from '../Pill';
import { InactiveCardView, OfferCardView } from './utils';

// Shared offer-card status pieces. The conversation thread header mirrors the offer card across
// states, so these live in one place to keep the card and the header from drifting apart.

const TopOfferBadge = () => (
  <Pill
    label="Top offer"
    bg="offerTopBadgeBG"
    color="greenDark"
    dotColor="leaseEndGreen"
    dotGlow="leaseEndGreenGlow"
  />
);

const CounterSentBadge = () => (
  <Pill
    label="Counter sent"
    bg="iceBlue"
    color="oceanBoatBlue"
    dotColor="oceanBoatBlue"
    dotGlow="oceanBoatBlueGlow"
  />
);

const ExpirationLine = ({ label, isUrgent }: { label: string; isUrgent: boolean }) => (
  <HStack
    as="span"
    spacing="4px"
    color={isUrgent ? 'leaseEndRed' : 'grayMedium'}
    fontSize="10.5px"
    fontWeight="600"
    letterSpacing="0.06em"
  >
    <Text as="span" color="grayMedium" userSelect="none">
      ·
    </Text>
    <Icon as={FaRegClock} boxSize="11px" />
    <span>{label}</span>
  </HStack>
);

type InactiveVariant = {
  pillLabel: string;
  icon: IconType;
  pillBg: string;
  pillColor: string;
  strip: (driverCounterAmount: number | null) => string;
};

export const INACTIVE_VARIANTS: Record<InactiveCardView['inactiveState'], InactiveVariant> = {
  rejected: {
    pillLabel: 'Declined',
    icon: FaTimesCircle,
    pillBg: 'offerInactivePillBG',
    pillColor: 'grayMedium',
    strip: (driverCounterAmount) => `Your ${formatMoney(driverCounterAmount)} counter was rejected`,
  },
  withdrawn: {
    pillLabel: 'Withdrawn',
    icon: FaUndo,
    pillBg: 'offerInactivePillBG',
    pillColor: 'grayMedium',
    strip: () => 'This offer was withdrawn',
  },
  expired: {
    pillLabel: 'Offer expired',
    icon: FaRegClock,
    pillBg: 'leaseEndRedBG',
    pillColor: 'leaseEndRed',
    strip: () => 'Offer expired before you responded',
  },
  closed: {
    pillLabel: 'Closed',
    icon: FaBan,
    pillBg: 'offerInactivePillBG',
    pillColor: 'grayMedium',
    strip: () => 'Closed when you accepted another offer',
  },
};

// All-in-one status badges for a dealer: picks which pills to show from the card's state, so the
// offer cards and the conversation header share one source of truth. Active cards get the top-offer
// badge, the counter-sent badge while waiting on the dealer, and the expiration countdown; inactive
// cards get their terminal status pill.
export const OfferStatusBadges = ({ card }: { card: OfferCardView | InactiveCardView }) => {
  if ('inactiveState' in card) {
    const { pillLabel, pillBg, pillColor } = INACTIVE_VARIANTS[card.inactiveState];
    return <Pill label={pillLabel} bg={pillBg} color={pillColor} />;
  }

  const isWaiting = card.driverCounterAmount != null;
  const expiration = isWaiting ? null : formatExpiresIn(card.offer.expiresAt);
  return (
    <>
      {card.isTopOffer && <TopOfferBadge />}
      {isWaiting && <CounterSentBadge />}
      {expiration && <ExpirationLine label={expiration.label} isUrgent={expiration.isUrgent} />}
    </>
  );
};
